// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/ui/AddressAutocompleteInput.tsx — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
import React, { useEffect, useRef, useState } from "react";
// App-contract imports — each app's hand-written adapter at src/lib/ binds its
// own lookups endpoint (physician /api/config/lookups vs patient
// /api/bridge/config/lookups) and re-exports the shared cores. med_form has no
// adapters, so this file is excluded from its sync (MED_FORM_EXCLUDES).
import { getAddressCompleteKey, findAddresses, retrieveAddress } from "../../lib/addressComplete";
import {
  getGooglePlacesKey,
  newSessionToken,
  findAddressesGoogle,
  retrieveAddressGoogle,
} from "../../lib/googlePlaces";

/** One address suggestion row (Canada Post or Google fallback). */
interface AddressSuggestion {
  id: string;
  text?: string;
  description?: string;
  isGoogle?: boolean;
  isRetrievable?: boolean;
}

/**
 * Resolved address record handed to onResolved. No index signature: the shared
 * cores (src/shared/lib) return the named AddressRecord/GoogleAddressRecord
 * interfaces, which carry no index signature — the five named keys are the
 * whole contract. `address` is required: both cores return it non-optional.
 */
interface ResolvedAddress {
  address: string;
  suite?: string;
  city?: string;
  province?: string;
  postal_code?: string;
}

interface AddressAutocompleteInputProps {
  value: string;
  onChange: (text: string) => void;
  onResolved: (rec: ResolvedAddress) => void;
  placeholder?: string;
  inputStyle?: React.CSSProperties;
}

/**
 * Street-address input with Canada Post AddressComplete suggestions.
 *
 * Feature-flagged by the bridge config: when no ADDRESSCOMPLETE_KEY is set the
 * component renders a plain input, byte-for-byte the old behavior. Picking a
 * suggestion fills street + suite + city + province + postal in one tap
 * (onResolved), which also kills the "typed the apartment into the street
 * line" failure mode.
 *
 * Module-level component (stable type) so it keeps input focus across parent
 * re-renders — do NOT inline this into a render-scoped closure.
 */
export default function AddressAutocompleteInput({
  value,
  onChange,          // (text) => void — raw typing
  onResolved,        // ({address, suite, city, province, postal_code}) => void
  placeholder,
  inputStyle,
}: AddressAutocompleteInputProps) {
  const [apiKey, setApiKey] = useState<string | null>(null);   // null = loading, "" = disabled
  const [items, setItems] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // True once a real search (key present, term ≥ 3 chars) came back with zero
  // suggestions — so we can reassure users whose (often new) street isn't yet in
  // Canada Post's list that plain typing still works.
  const [noResults, setNoResults] = useState(false);
  // Google Places (New) fallback — populated ONLY when Canada Post returns zero
  // top-level results and a Google key is configured. "" = disabled/off, so the
  // whole fallback is inert and behavior is byte-identical to CP-only.
  const [googleKey, setGoogleKey] = useState("");
  const [googleItems, setGoogleItems] = useState<AddressSuggestion[]>([]);
  const sessionTokenRef = useRef<string | null>(null);   // spans one typing session; consumed by details
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    getAddressCompleteKey().then((k) => { if (live) setApiKey(k); });
    // Fetch the Google key once on mount alongside the CP key (never per keystroke).
    getGooglePlacesKey().then((k) => { if (live) setGoogleKey(k); });
    return () => { live = false; };
  }, []);

  // Close on outside tap. Closing ends the typing session, so drop the Google
  // session token — the next search starts a fresh billable session.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        sessionTokenRef.current = null;
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Monotonic guard: a slow response (CP or Google) for an old term must never
  // clobber the results of a newer keystroke.
  const searchSeqRef = useRef(0);

  const search = (term: string, lastId = "") => {
    if (!apiKey || !term || term.trim().length < 3) {
      setItems([]);
      setGoogleItems([]);
      setOpen(false);
      setNoResults(false);
      return;
    }
    const seq = ++searchSeqRef.current;
    setBusy(true);
    findAddresses(apiKey, term, lastId)
      .then(async (res) => {
        if (seq !== searchSeqRef.current) return;   // stale — a newer search superseded us
        setItems(res);
        // Zero suggestions for a real query → keep the panel open. Only for a
        // top-level search, not a drill-in (a container always has more to expand).
        const empty = res.length === 0 && !lastId;
        if (empty && googleKey) {
          // Canada Post came up empty — try the Google fallback at exactly the
          // branch that would otherwise show the manual-entry hint. Any failure
          // degrades to [] so the hint shows exactly as today.
          if (!sessionTokenRef.current) sessionTokenRef.current = newSessionToken();
          const gres = await findAddressesGoogle(googleKey, term, sessionTokenRef.current);
          if (seq !== searchSeqRef.current) return;   // stale after the Google hop
          setGoogleItems(gres);
          // Keep the panel open either way: Google items to show, or the hint.
          setNoResults(gres.length === 0);
          setOpen(true);
        } else {
          setGoogleItems([]);
          setNoResults(empty);
          setOpen(res.length > 0 || empty);
        }
      })
      .catch(() => {
        if (seq !== searchSeqRef.current) return;
        setItems([]); setGoogleItems([]); setOpen(false); setNoResults(false);
      })
      .finally(() => { if (seq === searchSeqRef.current) setBusy(false); });
  };

  const handleType = (text: string) => {
    onChange(text);
    if (!apiKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(text), 250);
  };

  const pick = async (item: AddressSuggestion) => {
    if (item.isGoogle) {
      // Google fallback item — never route through the CP drill-down.
      setOpen(false);
      const token = sessionTokenRef.current || newSessionToken();
      const rec = await retrieveAddressGoogle(googleKey, item.id, token).catch(() => null);
      // A completed details call consumes the session (Google pricing) — reset.
      sessionTokenRef.current = null;
      if (rec && rec.address) {
        onResolved(rec);
      }
      return;
    }
    if (!item.isRetrievable) {
      // Container (e.g. a street) — drill in with the same term.
      search(value, item.id);
      return;
    }
    setOpen(false);
    const rec = await retrieveAddress(apiKey || "", item.id).catch(() => null);
    if (rec && rec.address) {
      onResolved(rec);
    }
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <input
        type="text"
        value={value}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => { if (items.length > 0 || googleItems.length > 0 || noResults) setOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
        style={inputStyle}
      />
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30,
          background: "#fff", border: "1px solid #d8c9b6", borderRadius: 8,
          marginTop: 4, boxShadow: "0 6px 18px rgba(24,42,51,0.14)",
          maxHeight: 240, overflowY: "auto",
        }}>
          {/* Manual-entry hint: only when CP AND the Google fallback are both empty. */}
          {noResults && items.length === 0 && googleItems.length === 0 && (
            <div style={{
              padding: "10px 12px", fontSize: 12.5, color: "#5b6e7c",
              lineHeight: 1.4, background: "#faf7f1",
            }}>
              Address not in Canada Post's list? Just keep typing — manual entry works.
            </div>
          )}
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => pick(it)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "9px 12px", background: "none", border: "none",
                borderBottom: "1px solid #f0e9dd", cursor: "pointer",
                fontSize: 14, color: "#182a33", lineHeight: 1.35,
              }}
            >
              {it.text}
              {it.description && (
                <span style={{ color: "#7e8c96", fontSize: 12 }}> — {it.description}</span>
              )}
            </button>
          ))}
          {/* Google Places (New) fallback — only present when CP returned zero.
              Divider labels the section; ToS requires the "powered by Google"
              attribution when Places data shows without a map. */}
          {googleItems.length > 0 && (
            <>
              <div style={{
                padding: "6px 12px", fontSize: 10.5, color: "#8a959d",
                textTransform: "uppercase", letterSpacing: 0.4,
                background: "#faf7f1", borderBottom: "1px solid #f0e9dd",
              }}>
                More results
              </div>
              {googleItems.map((it) => (
                <button
                  key={`g-${it.id}`}
                  type="button"
                  onClick={() => pick(it)}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "9px 12px", background: "none", border: "none",
                    borderBottom: "1px solid #f0e9dd", cursor: "pointer",
                    fontSize: 14, color: "#182a33", lineHeight: 1.35,
                  }}
                >
                  {it.text}
                  {it.description && (
                    <span style={{ color: "#7e8c96", fontSize: 12 }}> — {it.description}</span>
                  )}
                </button>
              ))}
            </>
          )}
          <div style={{ padding: "5px 12px", fontSize: 10, color: "#a8b3bb", textAlign: "right" }}>
            {busy
              ? "searching…"
              : googleItems.length > 0
                ? "address lookup by Canada Post · powered by Google"
                : "address lookup by Canada Post"}
          </div>
        </div>
      )}
    </div>
  );
}
